import { useEffect, useImperativeHandle, useRef, useState } from 'react'
import { reportError } from 'utils/error'
import { FileType } from 'types'
import { FolderClient } from 'api/clients/FolderClient'
import { SUCCESS_RESPONSE_TYPE } from 'api/constants'
import { useAuth } from 'hooks/UseAuth'
import { showToastMessage } from 'common/ToastMessage/ShowToastMessage'
import { CopiedItem, Destination, Item } from 'components/CopyModal/CopyModal'
import { Tooltip } from 'components/Tooltip/Tooltip'
import { forwardRef } from 'react'
import { SelectedFolder } from 'components/LocationPicker/LocationPicker'
import { ArticleClient } from 'api/clients/ArticleClient'
import { useWorkspace } from 'hooks/useWorkspace'
import { ALMANAC_CORE_WORKSPACE_ID } from 'utils/constants'
import { Modal } from 'almanac-ui/Modal'
import { useQueryClient } from 'react-query'
import { foldersKeys } from 'hooks/queries/folders/foldersKeys'
import { useFinderRefresh } from 'screens/Finder/hooks/useFinderRefresh'
import { useBulkCopyFiles } from 'hooks/useBulkCopyFiles'

export type SuccessfulCopyCallBack = (
  copiedItem: CopiedItem,
  destination: { workspaceId: number; selectedFolder: SelectedFolder | null },
) => void

export type CopyHandle = {
  copy: () => void
}
export interface CopyActionsProps {
  item: Item
  itemType?: FileType
  copyTitle: string
  emoji?: string | null
  destination: Destination | undefined
  onNameTaken: VoidFunction
  onSuccessfulCopy: SuccessfulCopyCallBack
  shareWithSamePeople: boolean
  onSubmit?: () => void
  label?: string
  progressLabel?: string
  fromTemplate?: boolean
  position?: number | null
}

export const CopyActions = forwardRef<CopyHandle, CopyActionsProps>(
  (
    {
      item,
      itemType = 'article',
      copyTitle,
      emoji,
      destination,
      onNameTaken,
      onSuccessfulCopy,
      shareWithSamePeople,
      onSubmit,
      label = 'Make a Copy',
      progressLabel = 'Copying..',
      fromTemplate = false,
      position,
    }: CopyActionsProps,
    ref: any,
  ) => {
    const auth = useAuth()
    const [copying, setCopying] = useState(false)
    const queryClient = useQueryClient()
    const refreshFinder = useFinderRefresh()
    const { startCopy, result } = useBulkCopyFiles()
    const mountedRef = useRef(true)
    useEffect(() => () => { mountedRef.current = false }, [])

    const selectedFolder = destination?.selectedFolder || null
    const selectedFolderId = selectedFolder?.id || null
    // TODO: Fix this the next time the file is edited.
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    const workspaceId = destination?.newWorkspaceId || auth?.company_id!
    const inWorkspace = !!useWorkspace(destination?.newWorkspaceId || null)

    const handleUnlinkedCopy = () => {
      let folderIds = []
      let folderNames = []
      let articleIds = []
      let articleNames = []
      let fileIds = []
      let fileNames = []

      switch (itemType) {
        case 'article':
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          articleIds.push(item.id!)
          articleNames.push(copyTitle)
          break
        case 'folder':
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          folderIds.push(item.id!)
          folderNames.push(copyTitle)
          break
        case 'media_file':
        case 'design':
          if (item.id) fileIds.push(item.id)
          fileNames.push(copyTitle)
          break
      }

      startCopy({
        folderIds,
        articleIds,
        articleNames,
        folderNames,
        fileIds,
        fileNames,
        destinationFolderId: selectedFolderId,
        destinationWorkspaceId: workspaceId,
        shareWithSamePeople,
        fromTemplate,
        position,
      })
    }

    useEffect(() => {
      if (!result) return

      const finalize = async () => {
        if (result.type === SUCCESS_RESPONSE_TYPE) {
          const { files } = result.data
          if (files.length === 0) {
            // Redis flush edge case — copy succeeded but result IDs expired from cache
            if (selectedFolderId) queryClient.invalidateQueries(foldersKeys.detail(selectedFolderId))
            refreshFinder()
            showToastMessage('Copy complete — check the destination folder to see new files.', 'success')
            return
          }

          const copiedItem = files[0]
          if (emoji) {
            switch (itemType) {
              case 'article':
                await ArticleClient.updateArticle(copiedItem.id, { header_emoji: emoji })
                break
              case 'folder':
                FolderClient.updateFolder(copiedItem.id, { emoji })
                break
            }
          }
          onSuccessfulCopy(copiedItem, { workspaceId, selectedFolder })
        } else {
          const reason = typeof result.data === 'string' ? result.data : null
          showToastMessage(
            reason
              ? `Copy failed: ${reason}`
              : 'An error occurred! Our team has been notified and we are looking into it.',
            'error',
          )
          if (!reason) reportError('Failed to copy unlinked document', { severity: 'error', metaData: { itemId: item.id } })
        }
      }

      finalize().finally(() => {
        if (mountedRef.current) setCopying(false)
      })
    }, [result]) // eslint-disable-line react-hooks/exhaustive-deps

    const handleCopy = () => {
      if (copying) {
        return
      }

      if (onSubmit || !item.id) {
        onSubmit?.()
        return
      }

      setCopying(true)
      handleUnlinkedCopy()
    }

    useImperativeHandle(ref, () => ({
      copy: () => handleCopy(),
    }))

    const isButtonDisabled = () => {
      return (
        copying ||
        !copyTitle.trim() ||
        (itemType === 'article' && !destination?.selectedFolder) ||
        destination?.selectedFolder?.id === item.id ||
        !inWorkspace ||
        workspaceId === ALMANAC_CORE_WORKSPACE_ID
      )
    }

    return (
      <Tooltip
        label="Docs can only live in folders. Select or create a new folder."
        placement="top"
        hide={!isButtonDisabled()}
      >
        <div>
          <Modal.FooterButton variant="primary" size="lg" isDisabled={isButtonDisabled()} onPress={handleCopy}>
            {copying ? progressLabel : label}
          </Modal.FooterButton>
        </div>
      </Tooltip>
    )
  },
)
